import { Location } from '@angular/common';
import { ChangeDetectorRef, Directive, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { IContentMeta } from '@app/library/content-card/content-meta/content-meta.component';
import {
  AccountService,
  AuthService,
  ContentService,
  ContextService,
  FeaturesService,
  FilesService,
  MetaService,
  PlatformService,
  RequestService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getMe } from '@store/reducers/profile.reducer';
import {
  Content,
  ContentDetailsType,
  FeatureEnum,
  FindCollectionStrategyEnum,
  getContentInstanceByItem,
  IAccountStats,
  IFindCollectionFilter,
  ImageSizeEnum,
  SubjectCategoryEnum,
  TenantEnum,
  User,
  UserRoleEnum,
} from 'lingo2-models';
import { OnUiCover } from 'onclass-ui';
import { first, takeUntil } from 'rxjs/operators';

export interface IContentCardState {
  cut: boolean;
  copied: boolean;
  subscribed: boolean;
  complainFormOpened: boolean;
}

export type ContentCardDisplayMode = 'default' | 'content_page';

@Directive()
// tslint:disable-next-line:directive-class-suffix
export class BaseContentCard extends ChangableComponent implements OnInit {
  @Input() set item(item: Content) {
    this._item = getContentInstanceByItem(item);
    this.init();
  }
  get item(): Content {
    return this._item;
  }
  @Input() show = true;
  @Input() canClose = false;
  @Input() inline = false;
  @Input() active = false;
  @Input() playAutomatically = false;
  @Input() fromCollection = false;
  @Input() set cut(cut: boolean) {
    this.state.cut = cut;
    this.detectChanges();
  }
  @Input() displayMode: ContentCardDisplayMode = 'default';
  // tslint:disable-next-line: no-output-native
  @Output() close = new EventEmitter<boolean>();
  @Output() favoriteChanged = new EventEmitter<boolean>();
  public state: IContentCardState = {
    cut: false,
    copied: false,
    subscribed: false,
    complainFormOpened: false,
  };
  public fullTitle: string;
  public level: string;
  public contentTypeTitle: string;
  public author: User;
  public authorId: string;
  public authorName: string;
  public authorFullName: string;
  public contentDate: Date;
  public coverImageUrl: string;
  public metaImageUrl: string;
  public title: string;
  public sub_title: string;
  public subject: string;
  public contentUrl: string;
  public subjectRoute: string;
  public contentTypeRoute: string;
  public me$ = this.contextService.me$;
  public me: User; // TODO проверить зачем он нужен
  public accountRoute = AccountService.accountRoute;
  public contentRoute = LibraryRouter.contentRouteUniversal;
  public subjectCategoryEnum = SubjectCategoryEnum;
  public isAccessGranted = false;
  public collectionsFilter: Partial<IFindCollectionFilter>;
  public authorImg: OnUiCover;
  public cover: OnUiCover;
  public stats: Partial<IAccountStats>;
  public lastActiveAt: Date;

  protected cutMaxLength = 214;
  protected _item: Content;
  protected _originalMeta;
  protected _originalUrl: string;

  public constructor(
    protected accountService: AccountService,
    protected contextService: ContextService,
    protected contentService: ContentService,
    protected authService: AuthService,
    protected features: FeaturesService,
    protected router: Router,
    protected metaService: MetaService,
    protected translate: TranslateService,
    protected location: Location,
    protected readonly request: RequestService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
    protected store: Store,
    public errorNotificationService?: ErrorNotificationService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.author = user;
          this.authorName = this.author ? AccountService.getUserName(this.author) : '';
          this.authorFullName = this.author ? AccountService.getUserFullName(this.author) : '';
          this.stats = this.author?.stats;
          this.lastActiveAt = new Date(this.author?.last_active_at);

          this.authorImg = {
            type: 'avatar',
            img: FilesService.getFileUrlBySize(user?.userpic_id, ImageSizeEnum.sm),
            title: this.authorFullName,
            link: AccountService.accountRoute(this.author).join('/'),
            form: 'circle',
          };
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected init() {
    if (this._item) {
      this.fullTitle = this.contentService.contentFullTitle(this._item);
    }
    if (!!this._item.level_other_name) {
      this.level = this._item.level_other_name;
    } else {
      this.level = this._item.level ? this._item.level.title : this.translate.instant('subject-levels.any-level');
    }
    this.authorId = this._item.author_id;
    this.loadAuthorDetails(this._item.author_id);
    this.contentTypeTitle = this._item ? ContentService.contentTypeTitle[this._item.type] : '';
    this.contentDate = this._item
      ? this._item.published_at
        ? new Date(this._item.published_at)
        : new Date(this._item.created_at)
      : new Date();
    this.prepareCoverImage();
    this.prepareMetaImage();

    this.title = this._item ? this._item.title : null;
    this.sub_title = this._item ? this._item.sub_title : null;

    this.subject = this._item ? (this._item.subject ? this._item.subject.title : null) || '---' : null;

    const _route = this._item ? this.contentRoute(this._item) : null;
    this.contentUrl = this.request.host + _route;

    const subjectRouter = new LibraryRouter();
    subjectRouter.setSubject(this._item.subject.code);
    this.subjectRoute = subjectRouter.getLink().path;

    const contentTypeRouter = new LibraryRouter();
    contentTypeRouter.setSubject(this._item.subject.code);
    contentTypeRouter.setContentTypeByItem(this._item.type);
    this.contentTypeRoute = contentTypeRouter.getLink().path;

    // ограничение доступа к контенту
    this.isAccessGranted = this._item?.visit_info?.is_access_granted;

    // для коммерческого контента - список коллекций для покупки доступа
    this.collectionsFilter =
      (this._item.collections || []).length > 0
        ? {
            use: FindCollectionStrategyEnum.library,
            id: this._item.collections.map((c) => c.id),
          }
        : null;

    this.cover = {
      type: 'cover',
      img: this.coverImageUrl,
      title: this.fullTitle,
      link: this.isMeVersion() ? null : this.contentRoute(this._item),
      form: 'base',
    };
    this.loadIsSubscribed();
    this.markForCheck();
  }

  protected prepareCoverImage() {
    this.coverImageUrl = FilesService.getFileUrlBySize(this.item.cover_id);
  }

  protected prepareMetaImage() {
    this.metaImageUrl = FilesService.getFileUrlBySize(this.item.cover_id);
  }

  protected reloadItemDetails(details: ContentDetailsType[]) {
    this.contentService
      .getById(this._item.id, false, details)
      .pipe(first())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (_item) => {
          Object.entries(_item).map(([key, value]) => {
            this._item[key] = value;
          });
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected getState(): IContentCardState {
    return this.state;
  }

  public get isAuthor(): boolean {
    return this.me && this._item && this.me.id === this._item.author_id;
  }

  public get isModerator(): boolean {
    return this.me && AccountService.hasRole(this.me, UserRoleEnum.moderator);
  }

  public get description(): string {
    const desc = this.item.description || '';

    return this.isBrowser && this.state.cut
      ? desc.length > this.cutMaxLength
        ? desc.substr(0, this.cutMaxLength) + '...'
        : desc
      : desc;
  }

  public get meta(): IContentMeta {
    return {
      contentUrl: this.contentUrl,
      fullTitle: this.fullTitle,
      contentDate: this.contentDate,
      metaImageUrl: this.metaImageUrl,
      coverImageUrl: this.coverImageUrl,
      description: this.description,
      languageCode: this.item?.language?.code,
      commentsCount: this.item?.stats?.comments_count,
    };
  }

  public closeCard() {
    this.close.emit();
  }

  public onShareAndStatsChange({ visit_info, stats }) {
    this._item.visit_info = { ...this._item.visit_info, ...visit_info };
    this._item.stats = { ...this._item.stats, ...stats };
    this.detectChanges();
  }

  public onCommentsChange(event) {
    if ('is_commented' in event) {
      this._item.visit_info = { ...this._item.visit_info, is_commented: event.is_commented };
    }
    if ('comments_count' in event) {
      this._item.stats = { ...this._item.stats, comments_count: event.comments_count };
    }
    this.detectChanges();
  }

  public setIsCommented() {
    this._item.visit_info = { ...this._item.visit_info, is_commented: true };
    this.detectChanges();
  }

  public onLessonReviewSubmitted() {
    // this._item.visit_info = { ...this._item.visit_info, is_commented };
    this.reloadItemDetails(['stats', 'visit_info']);
    this.detectChanges();
  }

  public onCopied(event) {
    if (!this.isBrowser) {
      return;
    }
    this.state.copied = true;
    this.detectChanges();

    this.setTimeout(() => {
      this.state.copied = false;
      this.detectChanges();
    }, 1000);
  }

  public toggleCut() {
    this.state.cut = !this.state.cut;
    this.detectChanges();
  }

  public openComplainForm() {
    if (this.isAuthUser) {
      this.state.complainFormOpened = true;
    } else {
      this.authService.showAuthModal(() => {
        this.state.complainFormOpened = true;
        this.detectChanges();
      });
    }
  }

  public get isAuthUser() {
    return this.authService.isAuthenticated;
  }

  public closeComplainForm() {
    this.state.complainFormOpened = false;
    this.detectChanges();
  }

  protected loadIsSubscribed() {
    if (!this.isBrowser) {
      return;
    }
    this.accountService
      .getIsSubscribed(this._item.author_id)
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.state.subscribed = result;
          this.detectChanges();
        },
        error: (error) => {
          this.state.subscribed = false;
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public unsubscribe() {
    this.accountService
      .unsubscribe(this._item.author_id)
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.state.subscribed = false;
          this.detectChanges();
        },
        error: (error) => {
          this.state.subscribed = false;
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected setupMeta() {
    if (!this._originalMeta) {
      this._originalMeta = {
        title: this.metaService.getTitle(),
      };
    }

    this.metaService.setTitle([this.contentService.contentFullTitle(this.item)]);

    const url = LibraryRouter.contentRouteUniversal(this.item);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      if (!this._originalUrl) {
        this._originalUrl = currentUrl;
      }
      this.location.replaceState(url);
    }
  }

  protected resetMeta() {
    if (this._originalMeta) {
      this.metaService.setTitle([this._originalMeta.title]);
      this._originalMeta = null;
    }

    if (this._originalUrl) {
      this.location.replaceState(this._originalUrl);
      this._originalUrl = null;
    }
  }

  public onFavoriteChanged() {
    this.favoriteChanged.emit(true);
  }

  public get isAvailableStatistics() {
    return this.features.isAvailable(FeatureEnum.statistics);
  }
}
